import { useRemoteClientPost, useRemoteClientPut } from '@/configs/http-client'
import { RoutesPath } from '@/configs/routes-path'
import { Endpoints } from '@/remote/endpoints'
import { useLocalStorage } from '@/remote/local-storage'
import { zodResolver } from '@hookform/resolvers/zod'
import { startTransition, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { ComboSelectWithLabel } from '../components/combo-select-with-label'
import { Button, Input } from '../components/ui/components'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { useToast } from '../hooks'
import { useNavigateRoute } from '../hooks/navigation'
import { UsersFormData, usersSchema } from '../validators/users-validator'

interface UsersFormProps {
  data?: any
  closeDialog?: () => void
  updateList?: () => void
}

export default function UsersForm({ data, closeDialog, updateList }: UsersFormProps) {
  const { httpClientPost } = useRemoteClientPost()
  const { httpClientPut } = useRemoteClientPut()
  const { showToast } = useToast()
  const { useFetchLocalStore, getEmail, clearStorage, getName, getUnitId, isProfileAdmin } = useLocalStorage()
  const [perfis, setPerfis] = useState<any>()
  const [dataUnits, setDataUnits] = useState<any>([])
  const [selectedUnits, setSelectedUnits] = useState<any>([])
  const { navigateTo } = useNavigateRoute()
  const [params] = useSearchParams()
  const isEditMode = Boolean(data?.id)

  const form = useForm<UsersFormData>({
    resolver: zodResolver(usersSchema),
    defaultValues: {
      nome: data?.nome || '',
      email: data?.email || '',
      senha: isEditMode ? 'Opus0001@' : '',
      perfis: data?.perfis ? { id: data.perfis[0].id, codigo: data.perfis[0].codigo } : { id: 0, codigo: '' },
      idAcademia: data?.idAcademia || useFetchLocalStore().idAcademia
    }
  })

  const onSubmit = async (formData: any) => {
    if (selectedUnits?.length === 0) {
      showToast('Selecione ao menos uma unidade', 'error')
      return
    }

    const formattedData = {
      ...formData,
      id: data?.id,
      academias: [
        {
          id: formData?.idAcademia
        }
      ],
      perfis: [
        {
          id: formData?.perfis?.id,
          codigo: formData?.perfis?.codigo
        }
      ],
      unidades: [...selectedUnits.map((item: any) => item?.value)],
      situacao: 'ATIVO'
    }

    try {
      if (isEditMode) {
        formattedData.senha = null
        const response = await httpClientPut(Endpoints.users, { ...formattedData, inclusao: new Date() })
        // console.log('usuário atualizado')
        // console.log(response.data.data)
        const { data } = response.data
        // console.log(data)
        // console.log(data?.email)
        // console.log(getEmail())
        if (data?.email === getEmail() || data?.nome === getName()) {
          console.log('atualizando local storage')

          startTransition(() => {
            clearStorage()
            navigateTo(RoutesPath.login)
          })
        }

        closeDialog?.()
        updateList?.()
      } else {
        delete formattedData.id
        await httpClientPost(Endpoints.users, { ...formattedData })
        //console.log(response.data.data)
      }

      showToast('Requisição enviada com sucesso!', 'success')
      form.reset()
      setSelectedUnits([])
    } catch (error: any) {
      console.error('Erro no envio:', error?.response?.data?.errors)
      const errors = error?.response?.data?.errors
      if (errors) {
        for (const key in errors) {
          showToast(errors[key], 'error')
        }
      }
      showToast('Erro ao enviar requisição', 'error')
    }
  }

  const fetchUnits = async () => {
    try {
      const response = await httpClientPost(`${Endpoints.units}/filtro?page=${0}&size=${100}&order=desc&sort=id`, {
        idUnidade: isProfileAdmin() ? null : (params.get('id') ?? getUnitId())
      })

      const units = response?.data?.data?.content
      //console.log(data)
      setDataUnits(
        units.map((unit: any) => ({
          ...unit,
          label: unit?.nomeFantasia,
          value: unit?.id
        }))
      )
      if (data) {
        setSelectedUnits(
          units
            .filter((unit: any) => data?.unidades?.includes(unit?.id))
            .map((unit: any) => ({
              ...unit,
              label: unit?.nomeFantasia,
              value: unit?.id
            }))
        )
      }
      //console.log(dataUnits)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  useEffect(() => {
    console.log('fetching units')
    fetchUnits()
  }, [])

  const formValues = form.watch()

  useEffect(() => {
    console.log(formValues)
  }, [formValues])

  const {
    formState: { errors }
  } = form

  useEffect(() => {
    console.log('Erros do formulário:', errors)
  }, [errors])

  const fetchPerfil = async () => {
    setPerfis([])

    try {
      const response = await httpClientPost(`${Endpoints.perfis}/filtro?page=${0}&size=${100}&order=desc&sort=id`, {
        codigos: ['ADMIN', 'ADMINISTRADOR', 'ALUNO', 'CAIXA', 'COMERCIAL', 'CONSULTOR', 'COORDENADOR', 'PERSONAL', 'PROFESSOR']
      })

      const data = response?.data?.data?.content || []
      setPerfis(
        data.map((elem: any) => ({
          ...elem,
          label: elem?.codigo === 'ADMINISTRADOR' ? 'Gerente' : elem?.codigo === 'ALUNO' ? 'Consultor - App' : elem?.codigo,
          value: elem?.id
        }))
      )
      console.log(data)
    } catch (error) {
      console.error('Erro ao buscar dados de serviços:', error)
    }
  }

  useEffect(() => {
    fetchPerfil()
  }, [])

  return (
    <Form {...form}>
      <form id="formUser" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 pl-1 pr-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="nome"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Nome completo" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="seu@email.com" type="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="senha"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Senha</FormLabel>
                <FormControl>
                  <Input {...field} type="password" placeholder="Senha" disabled={isEditMode} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="perfis.id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {' '}
                  <span className="text-gray-400">*</span> Perfil
                </FormLabel>
                <FormControl>
                  <ComboSelectWithLabel
                    defaultValue={field.value}
                    data={perfis}
                    onChange={(e) => {
                      console.log(e?.label)
                      form.setValue('perfis.codigo', e?.label)
                      form.setValue('perfis.id', e?.value)
                      form.trigger(['perfis.codigo', 'perfis.id'])
                    }}
                  />
                </FormControl>
                <FormMessage className="text-xs">{form.formState.errors?.perfis?.codigo?.message}</FormMessage>
              </FormItem>
            )}
          />
        </div>
        <Select
          isMulti
          options={dataUnits}
          className="basic-multi-select w-full h-fit border-black"
          styles={{
            valueContainer(base: any) {
              return {
                ...base,
                overflowY: 'hidden',
                height: 36
              }
            }
          }}
          defaultValue={selectedUnits || []}
          classNamePrefix="select"
          value={selectedUnits}
          onChange={(e: any) => {
            setSelectedUnits(e as any)
          }}
          placeholder="Selecione as unidades"
        />
        {!isEditMode && (
          <Button type="submit" className="bg-acelerafit-green-400 text-muted  font-bold hover:bg-acelerafit-green-50 mt-3 ">
            Enviar
          </Button>
        )}
      </form>
    </Form>
  )
}
